/**
 * Extract a simplified line-structuring view from a single page's raw_data.
 * Returns an array of strings (one per text_line) by concatenating the text of
 * all descendant text_unit nodes.  Falls back to the original object when the
 * hierarchical format is not recognised.
 */
export const extractLineStructuring = (rawData: any): string[] | any => {
  if (!rawData?.root || !rawData?.objects) return rawData;

  const objects: Record<string, any> = rawData.objects;

  const getTextForNode = (uuid: string): string => {
    const node = objects[uuid];
    if (!node) return "";
    if (node.kind === "text_unit" || node.kind === "text_unit_preview") {
      return node.text ?? "";
    }
    if (Array.isArray(node.children)) {
      return node.children.map(getTextForNode).filter(Boolean).join(" ");
    }
    return "";
  };

  const rootChildren: string[] = rawData.root?.children ?? [];
  return rootChildren
    .map((uuid: string) => {
      const node = objects[uuid];
      if (node?.kind === "text_line") return getTextForNode(uuid);
      return null;
    })
    .filter((l): l is string => l !== null && l.trim() !== "");
};

// Normalize graph from either old nested {chartok_coords, edges} or new flat {symbols, coords, edges}
const normalizeGraph = (
  g: any,
): { symbols: string[]; coords: number[][]; edges: number[][] } | null => {
  if (!g) return null;
  if (g.chartok_coords) {
    return {
      symbols: g.chartok_coords.symbols || [],
      coords: g.chartok_coords.coords || [],
      edges: g.edges || [],
    };
  }
  if (g.symbols) return g;
  return null;
};

// Transform new hierarchical JSON format to legacy flat objects array format
// For ImageDisplay: excludes text_unit_preview (shows original text_units and other objects)
export const transformToLegacyFormat = (data: any): any => {
  // If data already has objects array, return as-is (legacy format)
  if (data?.objects && Array.isArray(data.objects)) {
    console.log("transformToLegacyFormat: Already in legacy format");
    return data;
  }

  // If data has root property (new format), transform it
  if (data?.root && data?.objects) {
    const objects: any[] = [];

    // Iterate through all nodes in data.objects
    Object.keys(data.objects).forEach((nodeId) => {
      const node = data.objects[nodeId];

      // Skip nodes without kind
      if (!node?.kind) return;

      // Map "kind" to "cls" and extract relevant fields
      // EXCLUDE text_unit_preview from ImageDisplay
      if (node.bbox && node.kind !== "text_unit_preview") {
        const cls = node.kind.replace("_preview", ""); // e.g., "molecule_preview" -> "molecule"
        const metadata: any = {};

        const isMolecule =
          node.kind === "molecule" || node.kind === "molecule_preview";
        if (isMolecule) {
          // Molecules store SMILES in node.smiles (separate field from node.text)
          if (node.smiles !== undefined && node.smiles !== null) {
            metadata.smiles = node.smiles;
          } else if (node.text !== undefined && node.text !== null) {
            metadata.smiles = node.text;
          }
          // Extract graph for DRKit rendering
          if (node.graph) {
            const normalized = normalizeGraph(node.graph);
            if (normalized) metadata.graph = normalized;
          }
        } else if (node.text !== undefined && node.text !== null) {
          metadata.text = node.text;
        }

        objects.push({
          cls,
          bbox: node.bbox,
          metadata,
          uuid: nodeId, // Keep UUID for hover mapping
        });
      }
    });

    console.log(
      "transformToLegacyFormat: Transformed",
      objects.length,
      "objects",
    );
    return { objects };
  }

  // Return original data if format is unrecognized
  console.log("transformToLegacyFormat: Unrecognized format", data);
  return data;
};

// Transform for StructuredJSONDisplay: extracts text_lines with clusters and includes molecules
export const transformToPositionedFormat = (data: any): any => {
  // Legacy flat format: { objects: [...] }
  // Convert to positioned format so StructuredJSONDisplay can render it.
  if (data?.objects && Array.isArray(data.objects)) {
    console.log("transformToPositionedFormat: Converting legacy flat format");

    const textUnitsRaw: any[] = data.objects.filter(
      (o: any) => o.cls === "text_unit" && o.bbox && o.metadata?.text,
    );
    const moleculesRaw: any[] = data.objects.filter(
      (o: any) => (o.cls === "molecule" || o.cls === "rxn") && o.bbox,
    );

    // Dump graph structure to find SMILES location
    if (moleculesRaw.length > 0) {
      const m = moleculesRaw[0];
      console.log(
        "[transformToPositionedFormat] first molecule metadata.graph:",
        JSON.stringify(m.metadata?.graph),
      );
    }
    const tablesRaw: any[] = data.objects.filter(
      (o: any) => o.cls === "table" && o.bbox,
    );

    // Sort text units top-to-bottom, left-to-right
    textUnitsRaw.sort((a: any, b: any) => {
      const ay = (a.bbox[1] + a.bbox[3]) / 2;
      const by = (b.bbox[1] + b.bbox[3]) / 2;
      if (Math.abs(ay - by) > 0.005) return ay - by;
      return a.bbox[0] - b.bbox[0];
    });

    // Group into lines by y-centre proximity (within 0.012 of each other)
    const LINE_THRESHOLD = 0.012;
    const lineGroups: any[][] = [];
    textUnitsRaw.forEach((unit: any) => {
      const cy = (unit.bbox[1] + unit.bbox[3]) / 2;
      const existing = lineGroups.find((grp) => {
        const grpCy = (grp[0].bbox[1] + grp[0].bbox[3]) / 2;
        return Math.abs(cy - grpCy) < LINE_THRESHOLD;
      });
      if (existing) {
        existing.push(unit);
      } else {
        lineGroups.push([unit]);
      }
    });

    // Build textLines: each legacy text_unit → its own single-unit cluster
    const textLines: any[] = lineGroups.map((units: any[], lineIdx: number) => {
      const clusters = units.map((unit: any, uIdx: number) => ({
        uuid: unit.uuid || `legacy-cluster-${lineIdx}-${uIdx}`,
        textUnits: [
          {
            uuid: unit.uuid || `legacy-unit-${lineIdx}-${uIdx}`,
            bbox: unit.bbox,
            text: unit.metadata.text,
            originalTextUnit: unit.uuid,
          },
        ],
        originalX: unit.bbox[0],
        alignedX: unit.bbox[0],
      }));
      return { uuid: `legacy-line-${lineIdx}`, clusters };
    });

    // Build molecules list
    // SMILES can live in many places depending on backend version.
    // Only accept string values — metadata.graph may be an object.
    const strOrEmpty = (v: any): string => (typeof v === "string" ? v : "");
    const extractSmiles = (o: any): string =>
      strOrEmpty(o.smiles) ||
      strOrEmpty(o.metadata?.smiles) ||
      strOrEmpty(o.metadata?.graph?.smiles) ||
      strOrEmpty(o.metadata?.graph?.transcription) ||
      strOrEmpty(o.metadata?.graph?.canonical_smiles) ||
      strOrEmpty(o.text) ||
      strOrEmpty(o.metadata?.text) ||
      "";

    const molecules: any[] = [
      ...moleculesRaw.map((o: any) => {
        const [bx1, by1, bx2, by2] = o.bbox;
        return {
          cls: o.cls,
          bbox: o.bbox,
          text: extractSmiles(o),
          uuid: o.uuid || "",
          // Store the raw OCSR graph so the frontend can call the render endpoint
          graph: normalizeGraph(o.metadata?.graph),
          // Pass aspect-ratio hint derived from the bbox for de-normalizing coords
          bbox_width: bx2 - bx1,
          bbox_height: by2 - by1,
        };
      }),
      ...tablesRaw.map((o: any) => ({
        cls: "table",
        bbox: o.bbox,
        text: "",
        uuid: o.uuid || "",
        data: o.metadata?.data || o.data || {},
      })),
    ];

    // Estimate a standard_height from the median text unit height
    const heights = textUnitsRaw.map((u: any) => u.bbox[3] - u.bbox[1]);
    heights.sort((a: number, b: number) => a - b);
    const medianHeight = heights[Math.floor(heights.length / 2)] || 0.02;

    console.log(
      "transformToPositionedFormat: legacy molecule smiles sample:",
      molecules
        .slice(0, 3)
        .map((m) => ({ cls: m.cls, text: m.text, uuid: m.uuid })),
    );
    console.log(
      "transformToPositionedFormat: legacy → textLines:",
      textLines.length,
      "molecules:",
      molecules.length,
    );

    return {
      textLines,
      molecules,
      rxns: [],
      corefs: [],
      reactionChains: [],
      metadata: {
        standard_height: medianHeight,
        standard_spacing: medianHeight * 0.3,
      },
    };
  }

  // If data has root property (new format), transform it
  if (data?.root && data?.objects) {
    const textLines: any[] = [];
    const molecules: any[] = [];
    const rxns: any[] = [];
    const corefs: any[] = [];
    let globalMetadata: any = null;

    // Build mapping from original text_unit UUID to text_unit_preview bbox
    // This is needed because corefs and conditions reference original text_unit UUIDs,
    // but the text has been repositioned in text_unit_preview objects
    const originalToPreviewBbox: Map<string, number[]> = new Map();

    // First pass: build the mapping by scanning all text_unit_preview objects
    Object.entries(data.objects).forEach(([objId, obj]: [string, any]) => {
      if (
        obj?.kind === "text_unit_preview" &&
        obj.metadata?.original_text_unit &&
        obj.bbox
      ) {
        originalToPreviewBbox.set(obj.metadata.original_text_unit, obj.bbox);
      }
    });

    // Iterate through root children to find text_line objects
    const rootChildren = data.root.children || [];

    // Check if structured text_lines exist — if so, skip raw text_unit nodes
    // (the backend keeps both original text_units and new text_lines in
    // root.children; rendering both causes duplicate offset text)
    const hasTextLines = rootChildren.some((childId: string) => {
      const node = data.objects[childId];
      return node?.kind === "text_line";
    });

    rootChildren.forEach((childId: string) => {
      const node = data.objects[childId];
      if (!node) return;

      // Handle text_line objects
      if (node.kind === "text_line") {
        const clusters: any[] = [];

        // Store global metadata from first line
        if (!globalMetadata && node.metadata) {
          globalMetadata = node.metadata;
        }

        // Iterate through cluster children
        (node.children || []).forEach((clusterId: string) => {
          const clusterNode = data.objects[clusterId];
          if (!clusterNode || clusterNode.kind !== "text_cluster") return;

          const textUnits: any[] = [];
          const clusterMetadata = clusterNode.metadata || {};

          // Iterate through text_unit_preview children in cluster
          (clusterNode.children || []).forEach((previewId: string) => {
            const previewNode = data.objects[previewId];
            if (!previewNode || previewNode.kind !== "text_unit_preview")
              return;

            textUnits.push({
              uuid: previewId,
              bbox: previewNode.bbox,
              text: previewNode.text,
              parent: previewNode.parent,
              originalTextUnit: previewNode.metadata?.original_text_unit,
            });
          });

          if (textUnits.length > 0) {
            clusters.push({
              uuid: clusterId,
              textUnits,
              originalX:
                clusterMetadata.original_x || textUnits[0]?.bbox[0] || 0,
              alignedX:
                clusterMetadata.aligned_x ||
                clusterMetadata.original_x ||
                textUnits[0]?.bbox[0] ||
                0,
            });
          }
        });

        if (clusters.length > 0) {
          textLines.push({
            uuid: childId,
            clusters,
          });
        }
      }

      // Handle text_unit nodes directly in root (only when structuring failed or was skipped)
      if (
        !hasTextLines &&
        node.kind === "text_unit" &&
        node.bbox &&
        node.text
      ) {
        textLines.push({
          uuid: childId,
          clusters: [
            {
              uuid: childId + "-cluster",
              textUnits: [
                {
                  uuid: childId,
                  bbox: node.bbox,
                  text: node.text,
                  parent: undefined,
                  originalTextUnit: childId,
                },
              ],
              originalX: node.bbox[0],
              alignedX: node.bbox[0],
            },
          ],
        });
      }

      // Handle molecules at root level
      if (node.kind === "molecule" && node.bbox) {
        const [bx1, by1, bx2, by2] = node.bbox;
        molecules.push({
          cls: "molecule",
          bbox: node.bbox,
          text: node.smiles || node.text || "",
          uuid: childId,
          graph: normalizeGraph(node.graph),
          bbox_width: bx2 - bx1,
          bbox_height: by2 - by1,
        });
      }

      // Handle rxns at root level
      if (node.kind === "rxn" && node.bbox) {
        // Helper to resolve reaction component - could be object with bbox or UUID string
        const resolveComponent = (item: any) => {
          if (typeof item === "string") {
            // It's a UUID - look up the object
            const referencedObj = data.objects[item];
            if (referencedObj && referencedObj.bbox) {
              const [bx1, by1, bx2, by2] = referencedObj.bbox;
              return {
                bbox: referencedObj.bbox,
                category: "[Mol]",
                uuid: item,
                smiles: referencedObj.smiles || "",
                graph: normalizeGraph(referencedObj.graph),
                bboxWidth: bx2 - bx1,
                bboxHeight: by2 - by1,
              };
            }
            return null;
          }
          // It's already an object with bbox - also resolve uuids for text
          if (item.uuids && Array.isArray(item.uuids)) {
            // Resolve text from text units and get preview bboxes
            const conditionTextUnitBboxes: { uuid: string; bbox: number[] }[] =
              [];
            const texts = item.uuids
              .map((uuid: string) => {
                const textUnit = data.objects[uuid];
                // Use the text_unit_preview bbox if available, otherwise fall back to original
                const previewBbox = originalToPreviewBbox.get(uuid);
                if (previewBbox) {
                  conditionTextUnitBboxes.push({ uuid, bbox: previewBbox });
                } else if (textUnit?.bbox) {
                  conditionTextUnitBboxes.push({ uuid, bbox: textUnit.bbox });
                }
                return textUnit?.text || "";
              })
              .filter(Boolean);
            item.resolvedText = texts.join(" ");
            item.resolvedTextUuids = item.uuids;
            item.conditionTextUnitBboxes = conditionTextUnitBboxes;
          }
          return item;
        };

        const rxnData = {
          cls: "rxn",
          bbox: node.bbox,
          text: node.text,
          uuid: childId,
          reactants: (node.reactants || [])
            .map(resolveComponent)
            .filter(Boolean),
          products: (node.products || []).map(resolveComponent).filter(Boolean),
          conditions: (node.conditions || [])
            .map(resolveComponent)
            .filter(Boolean),
        };
        rxns.push(rxnData);
        molecules.push(rxnData);
      }

      // Handle moldetect_coref at root level
      if (node.kind === "moldetect_coref" && node.bbox) {
        // Resolve molecule UUID
        let moleculeData = node.molecule;
        if (typeof moleculeData === "string") {
          const mol = data.objects[moleculeData];
          if (mol) {
            moleculeData = {
              bbox: mol.bbox,
              uuid: moleculeData,
              smiles: mol.smiles || "",
            };
          }
        }

        // Resolve coref text UUIDs and collect individual text unit preview bboxes
        let corefData = node.coref;
        let corefText = "";
        let corefTextUuids: string[] = [];
        const corefTextUnitBboxes: { uuid: string; bbox: number[] }[] = [];

        if (Array.isArray(corefData)) {
          corefTextUuids = corefData;
          const texts = corefData
            .map((uuid: string) => {
              const textUnit = data.objects[uuid];
              // Use the text_unit_preview bbox if available, otherwise fall back to original
              const previewBbox = originalToPreviewBbox.get(uuid);
              if (previewBbox) {
                corefTextUnitBboxes.push({ uuid, bbox: previewBbox });
              } else if (textUnit?.bbox) {
                corefTextUnitBboxes.push({ uuid, bbox: textUnit.bbox });
              }
              return textUnit?.text || "";
            })
            .filter(Boolean);
          corefText = texts.join(" ");
        } else if (typeof corefData === "object" && corefData?.bbox) {
          // Still has bbox, not yet merged - use that bbox for text
          corefTextUnitBboxes.push({
            uuid: "coref-bbox",
            bbox: corefData.bbox,
          });
        }

        corefs.push({
          cls: "moldetect_coref",
          bbox: node.bbox,
          uuid: childId,
          molecule: moleculeData,
          coref: corefData,
          corefText,
          corefTextUuids,
          corefTextUnitBboxes, // Individual bboxes for each text unit (using preview positions)
        });
      }

      // Handle tables at root level
      if (node.kind === "table" && node.bbox) {
        molecules.push({
          cls: "table",
          bbox: node.bbox,
          text: node.text,
          uuid: childId,
          data: node.data || {},
        });
      }
    });

    // Build reaction chains
    const reactionChains = buildReactionChains(rxns, molecules, corefs);

    console.log(
      "transformToPositionedFormat: Extracted",
      textLines.length,
      "text lines,",
      molecules.length,
      "molecules/rxns,",
      corefs.length,
      "corefs,",
      reactionChains.length,
      "reaction chains",
    );
    console.log(
      "transformToPositionedFormat: Chains:",
      reactionChains.map((c) => ({
        id: c.id,
        components: c.allComponents?.length,
        color: c.color,
      })),
    );
    return {
      textLines,
      molecules,
      rxns,
      corefs,
      reactionChains,
      metadata: globalMetadata,
    };
  }

  // Return original data if format is unrecognized
  console.log("transformToPositionedFormat: Unrecognized format", data);
  return data;
};

// Color palette for reaction chains
const CHAIN_COLORS = [
  "rgba(59, 130, 246, 0.25)", // blue
  "rgba(16, 185, 129, 0.25)", // green
  "rgba(245, 158, 11, 0.25)", // amber
  "rgba(239, 68, 68, 0.25)", // red
  "rgba(139, 92, 246, 0.25)", // purple
  "rgba(236, 72, 153, 0.25)", // pink
  "rgba(6, 182, 212, 0.25)", // cyan
  "rgba(132, 204, 22, 0.25)", // lime
];

// Build reaction chains by matching products to reactants via IoU
function buildReactionChains(
  rxns: any[],
  molecules: any[],
  corefs: any[],
): any[] {
  if (rxns.length === 0) {
    // No reactions - create chains for standalone molecules with corefs
    const chains: any[] = [];
    const moleculesInCorefs = new Set<string>();

    corefs.forEach((coref, idx) => {
      if (coref.molecule?.uuid) {
        moleculesInCorefs.add(coref.molecule.uuid);
        chains.push({
          id: `standalone-${idx}`,
          color: CHAIN_COLORS[idx % CHAIN_COLORS.length],
          rxns: [],
          molecules: [coref.molecule.uuid],
          corefs: [coref],
          conditionTexts: [],
          allComponents: [
            {
              type: "molecule",
              uuid: coref.molecule.uuid,
              bbox: coref.molecule.bbox,
            },
            { type: "coref", data: coref },
          ],
        });
      }
    });

    // Add molecules not in any coref
    const standaloneStart = chains.length;
    molecules.forEach((mol, idx) => {
      if (mol.cls === "molecule" && !moleculesInCorefs.has(mol.uuid)) {
        chains.push({
          id: `molecule-${idx}`,
          color: CHAIN_COLORS[(standaloneStart + idx) % CHAIN_COLORS.length],
          rxns: [],
          molecules: [mol.uuid],
          corefs: [],
          conditionTexts: [],
          allComponents: [{ type: "molecule", uuid: mol.uuid, bbox: mol.bbox }],
        });
      }
    });

    return chains;
  }

  // Build graph: find which reactions are connected
  // Two reactions are connected if a product of one overlaps significantly with a reactant of another

  // Helper to compute IoU
  const computeIoU = (
    bbox1: number[] | undefined,
    bbox2: number[] | undefined,
  ): number => {
    if (!bbox1 || !bbox2 || bbox1.length !== 4 || bbox2.length !== 4) return 0;

    const b1x1 = bbox1[0]!;
    const b1y1 = bbox1[1]!;
    const b1x2 = bbox1[2]!;
    const b1y2 = bbox1[3]!;
    const b2x1 = bbox2[0]!;
    const b2y1 = bbox2[1]!;
    const b2x2 = bbox2[2]!;
    const b2y2 = bbox2[3]!;

    const x1 = Math.max(b1x1, b2x1);
    const y1 = Math.max(b1y1, b2y1);
    const x2 = Math.min(b1x2, b2x2);
    const y2 = Math.min(b1y2, b2y2);

    if (x2 <= x1 || y2 <= y1) return 0;

    const intersection = (x2 - x1) * (y2 - y1);
    const area1 = (b1x2 - b1x1) * (b1y2 - b1y1);
    const area2 = (b2x2 - b2x1) * (b2y2 - b2y1);
    const union = area1 + area2 - intersection;

    return union > 0 ? intersection / union : 0;
  };

  // Build adjacency list for reactions
  const adjacency: Map<number, Set<number>> = new Map();
  rxns.forEach((_, idx) => adjacency.set(idx, new Set()));

  // Check each pair of reactions
  for (let i = 0; i < rxns.length; i++) {
    for (let j = i + 1; j < rxns.length; j++) {
      // Check if any product of i matches any reactant of j (or vice versa)
      let connected = false;

      // Products of i vs reactants of j
      for (const product of rxns[i].products || []) {
        for (const reactant of rxns[j].reactants || []) {
          if (product.uuid && reactant.uuid && product.uuid === reactant.uuid) {
            connected = true;
            break;
          }
          if (
            product.bbox &&
            reactant.bbox &&
            computeIoU(product.bbox, reactant.bbox) > 0.5
          ) {
            connected = true;
            break;
          }
        }
        if (connected) break;
      }

      // Products of j vs reactants of i
      if (!connected) {
        for (const product of rxns[j].products || []) {
          for (const reactant of rxns[i].reactants || []) {
            if (
              product.uuid &&
              reactant.uuid &&
              product.uuid === reactant.uuid
            ) {
              connected = true;
              break;
            }
            if (
              product.bbox &&
              reactant.bbox &&
              computeIoU(product.bbox, reactant.bbox) > 0.5
            ) {
              connected = true;
              break;
            }
          }
          if (connected) break;
        }
      }

      if (connected) {
        adjacency.get(i)!.add(j);
        adjacency.get(j)!.add(i);
      }
    }
  }

  // Find connected components using BFS
  const visited = new Set<number>();
  const components: number[][] = [];

  for (let i = 0; i < rxns.length; i++) {
    if (visited.has(i)) continue;

    const component: number[] = [];
    const queue = [i];

    while (queue.length > 0) {
      const curr = queue.shift()!;
      if (visited.has(curr)) continue;
      visited.add(curr);
      component.push(curr);

      adjacency.get(curr)!.forEach((neighbor) => {
        if (!visited.has(neighbor)) {
          queue.push(neighbor);
        }
      });
    }

    components.push(component);
  }

  // Build chain objects
  const chains: any[] = components.map((component, idx) => {
    const chainRxns = component.map((i) => rxns[i]);
    const chainMoleculeUuids = new Set<string>();
    const addedMoleculeUuids = new Set<string>(); // Track which molecules have been added to avoid duplicates
    const chainConditionTexts: any[] = [];
    const allComponents: any[] = [];

    // Collect all molecules and conditions from reactions in this chain
    // Deduplicate molecules that appear as both reactant and product in multi-step reactions
    chainRxns.forEach((rxn) => {
      allComponents.push({ type: "rxn", uuid: rxn.uuid, bbox: rxn.bbox });

      (rxn.reactants || []).forEach((r: any) => {
        if (r.uuid) {
          chainMoleculeUuids.add(r.uuid);
          // Only add this molecule component if we haven't added it yet
          if (!addedMoleculeUuids.has(r.uuid)) {
            addedMoleculeUuids.add(r.uuid);
            allComponents.push({
              type: "reactant",
              data: r,
              bbox: r.bbox,
              category: r.category,
            });
          }
        } else {
          // No UUID means it's not a molecule reference, add it anyway
          allComponents.push({
            type: "reactant",
            data: r,
            bbox: r.bbox,
            category: r.category,
          });
        }
      });
      (rxn.products || []).forEach((p: any) => {
        if (p.uuid) {
          chainMoleculeUuids.add(p.uuid);
          // Only add this molecule component if we haven't added it yet
          if (!addedMoleculeUuids.has(p.uuid)) {
            addedMoleculeUuids.add(p.uuid);
            allComponents.push({
              type: "product",
              data: p,
              bbox: p.bbox,
              category: p.category,
            });
          }
        } else {
          allComponents.push({
            type: "product",
            data: p,
            bbox: p.bbox,
            category: p.category,
          });
        }
      });
      (rxn.conditions || []).forEach((c: any) => {
        allComponents.push({
          type: "condition",
          data: c,
          bbox: c.bbox,
          category: c.category,
        });
        if (c.resolvedText) {
          chainConditionTexts.push({
            text: c.resolvedText,
            textUuids: c.resolvedTextUuids || [],
            bbox: c.bbox,
          });
        }
      });
    });

    // Find corefs that reference molecules in this chain
    const chainCorefs = corefs.filter((coref) => {
      const molUuid =
        typeof coref.molecule === "string"
          ? coref.molecule
          : coref.molecule?.uuid;
      return molUuid && chainMoleculeUuids.has(molUuid);
    });

    chainCorefs.forEach((coref) => {
      allComponents.push({ type: "coref", data: coref });
    });

    return {
      id: `chain-${idx}`,
      color: CHAIN_COLORS[idx % CHAIN_COLORS.length],
      rxns: chainRxns,
      molecules: Array.from(chainMoleculeUuids),
      corefs: chainCorefs,
      conditionTexts: chainConditionTexts,
      allComponents,
    };
  });

  // Handle molecules not in any reaction chain
  const moleculesInChains = new Set<string>();
  chains.forEach((chain) => {
    chain.molecules.forEach((uuid: string) => moleculesInChains.add(uuid));
  });

  // Find corefs for standalone molecules
  const standaloneMolecules = molecules.filter(
    (mol) => mol.cls === "molecule" && !moleculesInChains.has(mol.uuid),
  );

  const usedCorefs = new Set<string>();
  chains.forEach((chain) => {
    chain.corefs.forEach((c: any) => usedCorefs.add(c.uuid));
  });

  standaloneMolecules.forEach((mol, idx) => {
    const molCorefs = corefs.filter((coref) => {
      if (usedCorefs.has(coref.uuid)) return false;
      const molUuid =
        typeof coref.molecule === "string"
          ? coref.molecule
          : coref.molecule?.uuid;
      return molUuid === mol.uuid;
    });

    molCorefs.forEach((c) => usedCorefs.add(c.uuid));

    const allComponents: any[] = [
      { type: "molecule", uuid: mol.uuid, bbox: mol.bbox },
    ];
    molCorefs.forEach((coref) => {
      allComponents.push({ type: "coref", data: coref });
    });

    chains.push({
      id: `standalone-${idx}`,
      color: CHAIN_COLORS[(components.length + idx) % CHAIN_COLORS.length],
      rxns: [],
      molecules: [mol.uuid],
      corefs: molCorefs,
      conditionTexts: [],
      allComponents,
    });
  });

  return chains;
}

// Build mapping from original text_unit UUID to its text_unit_preview UUIDs
export const buildHoverMapping = (data: any): Map<string, string[]> => {
  const mapping = new Map<string, string[]>();

  if (!data?.root || !data?.objects) {
    return mapping;
  }

  // Find all text_unit_preview objects and map them by their original_text_unit
  Object.keys(data.objects).forEach((key) => {
    const node = data.objects[key];
    if (
      node?.kind === "text_unit_preview" &&
      node.metadata?.original_text_unit
    ) {
      const originalTextUnit = node.metadata.original_text_unit;
      if (!mapping.has(originalTextUnit)) {
        mapping.set(originalTextUnit, []);
      }
      mapping.get(originalTextUnit)!.push(key);
    }
  });

  return mapping;
};
